import React, { useEffect } from 'react';
import { useAnalysisContext, ResultTab } from '../../../context/AnalysisContext';
import { useResultsExport } from '../../../hooks/useResultsExport';
import { OverviewTab } from './tabs/OverviewTab';
import { TableTab } from './tabs/TableTab';
import { ExportTab } from './tabs/ExportTab';
import { LoadingState } from './components/LoadingState';
import { ErrorState } from './components/ErrorState';
import {
  BarChart3,
  Table2,
  Download,
  PlayCircle,
  TrendingUp,
} from 'lucide-react';

/**
 * Step 4: Identified Targets
 *
 * Simplified for target identification workflow.
 * Shows:
 * - Overview: Summary of identified targets
 * - Targets: Sortable table of lncRNA candidates
 * - Export: CSV/JSON export
 *
 * Removed: Plots, Network, Knockdown (these are follow-up actions on specific targets)
 */

interface TabConfig {
  id: ResultTab;
  label: string;
  icon: React.ReactNode;
  component: React.ComponentType;
}

const TABS: TabConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <BarChart3 className="w-4 h-4" />,
    component: OverviewTab,
  },
  {
    id: 'table',
    label: 'Targets',
    icon: <Table2 className="w-4 h-4" />,
    component: TableTab,
  },
  {
    id: 'export',
    label: 'Export',
    icon: <Download className="w-4 h-4" />,
    component: ExportTab,
  },
];

export const Step4ResultsView: React.FC = () => {
  const {
    selectedDisease,
    selectedCellTypes,
    preset,
    analysisResult,
    isAnalyzing,
    analysisError,
    activeResultTab,
    setActiveResultTab,
    runAnalysis,
  } = useAnalysisContext();

  const { hasResults } = useResultsExport();

  // ========================================
  // AUTO-TRIGGER ANALYSIS ON MOUNT
  // ========================================

  useEffect(() => {
    // Auto-run analysis when we navigate to this step
    // Check if we need to run analysis (no results yet, not currently analyzing, and no error)
    if (!analysisResult && !isAnalyzing && !analysisError) {
      console.log('[Step4] Auto-triggering analysis');
      runAnalysis();
    }
  }, [analysisResult, isAnalyzing, analysisError, runAnalysis]); // Run when dependencies change

  // ========================================
  // HANDLERS
  // ========================================

  const handleRunAnalysis = () => {
    runAnalysis();
  };

  // ========================================
  // RENDER CONTENT
  // ========================================

  const renderContent = () => {
    // Loading state
    if (isAnalyzing) {
      return <LoadingState />;
    }

    // Error state
    if (analysisError) {
      return <ErrorState error={analysisError} onRetry={handleRunAnalysis} />;
    }

    // No results yet
    if (!hasResults) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-xl">
            <div className="w-24 h-24 mx-auto mb-6 bg-[var(--origin-blue-500)]/10 rounded-full flex items-center justify-center">
              <TrendingUp className="w-12 h-12 text-[var(--origin-blue-500)]" />
            </div>
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
              Ready to Discover Targets
            </h3>
            <p className="text-[var(--text-secondary)] mb-6 text-lg">
              Configuration complete. Click "Run Analysis" to identify disease-specific lncRNA targets.
            </p>

            {/* Configuration Summary */}
            <div className="origin-card p-6 mb-6 text-left">
              <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                Analysis Summary
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Disease:</span>
                  <span className="font-medium text-[var(--text-primary)]">{selectedDisease}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Cell Types:</span>
                  <span className="font-medium text-[var(--text-primary)]">
                    {selectedCellTypes.disease.length + selectedCellTypes.normal.length} selected
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Method:</span>
                  <span className="font-medium text-[var(--text-primary)]">
                    Target Discovery (Correlation-based)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Preset:</span>
                  <span className="font-medium text-[var(--text-primary)] capitalize">
                    {preset}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleRunAnalysis}
              className="origin-btn origin-btn-primary text-lg px-8 py-3"
            >
              <PlayCircle className="w-5 h-5 mr-2" />
              Run Analysis
            </button>
          </div>
        </div>
      );
    }

    // Results with tabs
    const ActiveTabComponent = TABS.find((t) => t.id === activeResultTab)?.component || OverviewTab;

    return (
      <div className="flex-1 flex flex-col">
        {/* Tab Navigation */}
        <div className="border-b border-[var(--border-primary)] px-6">
          <div className="flex items-center gap-1 -mb-px">
            {TABS.map((tab) => {
              const isActive = activeResultTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveResultTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
                    isActive
                      ? 'border-[var(--origin-blue-500)] text-[var(--origin-blue-500)]'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-primary)]'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto">
          <ActiveTabComponent />
        </div>
      </div>
    );
  };

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <div className="flex-1 flex flex-col">
      {renderContent()}
    </div>
  );
};

export default Step4ResultsView;
